import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import aiAutomationApi from '../../utils/aiAutomationApi';
import { getMetadata } from '../../utils/encryptedStorage';
import CandidateDetail from './CandidateDetail';
import RejectCandidateModal from '../../components/InterviewScheduler/RejectCandidateModal';
import ScoreDetailsInfo from '../../components/ScoreDetailsInfo';
import ScoreEvidenceModal from '../../components/ScoreEvidenceModal';

/**
 * CandidateList — Candidate table for Overview section with accordion-expandable rows.
 * Clicking a row expands the full CandidateDetail inline below the row.
 */
const normalizeBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return Boolean(value);
};

const CandidateList = ({ searchId, sessionId, onCandidateClick, activeTab, jobTitle, onSidebarCollapse, onCandidateUpdate, onBack, refreshToken = 0, featureAccess = null }) => {
  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';

  // Data state
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [emailReplies, setEmailReplies] = useState({});
  const [candidatesByStatus, setCandidatesByStatus] = useState({
    interested: [],
    pending: [],
    declined: [],
  });

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [scoreEvidenceOpen, setScoreEvidenceOpen] = useState(false);
  const initialLoadDone = useRef(false);
  const searchDebounceRef = useRef(null);
  const pageCacheRef = useRef({});

  // Column key → backend sort field mapping
  const columnSortKeys = {
    candidate: 'name',
    match: 'match_percentage',
    source: 'source',
    status: 'status',
    resume_stats: 'resume_status',
    experience: 'experience',
    tags: 'tags',
    last_action: 'updated_at',
    next_step: 'status',
    contact: 'contact',
    last_updated: 'updated_at',
  };

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'interested', label: 'Interested' },
    { key: 'shortlisted', label: 'Shortlisted' },
    { key: 'interviewed', label: 'Interviewed' },
    { key: 'declined', label: 'Declined' },
    { key: 'rejected', label: 'Rejected' },
  ];

  // ── Source icon helper ──
  const SourceIcon = ({ source }) => {
    const src = (source || '').toLowerCase();
    if (src.includes('linkedin')) {
      return (
        <span title="LinkedIn" className="inline-flex items-center justify-center w-6 h-6 rounded" style={{ backgroundColor: '#0A66C2' }}>
          <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
        </span>
      );
    }
    if (src.includes('github')) {
      return (
        <span title="GitHub" className="inline-flex items-center justify-center w-6 h-6 rounded bg-gray-800 dark:bg-gray-700">
          <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/></svg>
        </span>
      );
    }
    if (src.includes('naukri')) {
      return (
        <span title="Naukri" className="inline-flex items-center justify-center w-6 h-6 rounded" style={{ backgroundColor: '#22C55E' }}>
          <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
        </span>
      );
    }
    if (src.includes('google')) {
      return (
        <span title="Google Jobs" className="inline-flex items-center justify-center w-6 h-6 rounded" style={{ backgroundColor: '#FBBC05' }}>
          <span className="text-white text-xs font-bold">G</span>
        </span>
      );
    }
    if (src.includes('email')) {
      return (
        <span title="Email" className="inline-flex items-center justify-center w-6 h-6 rounded" style={{ backgroundColor: '#EA4335' }}>
          <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
        </span>
      );
    }
    if (src.includes('import')) {
      return (
        <span title="Imported" className="inline-flex items-center justify-center w-6 h-6 rounded" style={{ backgroundColor: '#7C3AED' }}>
          <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
        </span>
      );
    }
    // Default
    return (
      <span title={source || 'Unknown'} className="inline-flex items-center justify-center w-6 h-6 rounded bg-gray-300 dark:bg-gray-600">
        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9"/></svg>
      </span>
    );
  };

  // ── Status badge ──
  const StatusBadge = ({ status }) => {
    const s = (status || 'pending').toLowerCase();
    const config = {
      sourced: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400', label: 'Sourced' },
      contacted: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'Contacted' },
      interested: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Interested' },
      shortlisted: { bg: '#E6FCF5', text: '#20C997', label: 'Shortlisted', custom: true },
      interviewed: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', label: 'Interviewed' },
      declined: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', label: 'Declined' },
      rejected: { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-700 dark:text-rose-400', label: 'Rejected' },
      imported: { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-400', label: 'Imported' },
      // Legacy mappings
      identified: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400', label: 'Sourced' },
      responded: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Interested' },
      interview: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', label: 'Interview' },
      'interview scheduled': { bg: '#E6FCF5', text: '#20C997', label: 'Shortlisted', custom: true },
      finalised: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', label: 'Interviewed' },
      pending: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400', label: 'Sourced' },
    };
    const c = config[s] || config.pending;
    return (
      <span 
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${c.custom ? '' : c.bg + ' ' + c.text}`}
        style={c.custom ? { backgroundColor: c.bg, color: c.text } : {}}>
        {c.icon && (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        )}
        {c.label}
      </span>
    );
  };

  // ── Format relative time ──
  const formatTimeAgo = (date) => {
    if (!date) return '—';
    const now = new Date();
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    const diffSec = Math.floor((now - d) / 1000);
    if (diffSec < 60) return 'Just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    const days = Math.floor(diffSec / 86400);
    return days === 1 ? '1d ago' : `${days}d ago`;
  };

  // ── Format source string ──
  const formatSource = (src) => {
    if (!src || typeof src !== 'string') return 'Unknown';
    return src.replace(/[_-]/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  };

  const handleCandidateUpdate = useCallback((email, updates = {}) => {
    if (email && Object.prototype.hasOwnProperty.call(updates, 'priority')) {
      const emailLower = String(email).toLowerCase();
      const priority = normalizeBoolean(updates.priority);

      setCandidates((current) => current.map((candidate) => (
        String(candidate.email || '').toLowerCase() === emailLower
          ? { ...candidate, priority }
          : candidate
      )));

      Object.keys(pageCacheRef.current || {}).forEach((key) => {
        const cache = pageCacheRef.current[key];
        if (!Array.isArray(cache?.allData)) return;
        pageCacheRef.current[key] = {
          ...cache,
          allData: cache.allData.map((candidate) => (
            String(candidate?.email || '').toLowerCase() === emailLower
              ? { ...candidate, priority }
              : candidate
          )),
        };
      });
    }

    onCandidateUpdate?.(email, updates);
  }, [onCandidateUpdate]);

  // ── Smart caching: accumulates fetched data per filter key ──
  // Page navigation: fetches page=N&limit=L (standard offset-based).
  // Limit change: if smaller limit, slices from accumulated cache (no API call).
  // E.g., fetched 25 → switching to 10 serves first 10 from cache.
  const loadCandidates = useCallback(async (silent = false) => {
    const cacheKey = `${debouncedSearchTerm}__${activeFilter}__${sortBy}__${sortOrder}`;
    const cache = pageCacheRef.current[cacheKey];
    const offset = (currentPage - 1) * itemsPerPage;
    const needEnd = offset + itemsPerPage;

    // Smart cache: serve from accumulated data if we already have enough
    if (cache && cache.allData) {
      const hasEnough = needEnd <= cache.allData.length;
      const allFetched = cache.allData.length >= cache.totalRecords;
      if (hasEnough || allFetched) {
        const sliced = cache.allData.slice(offset, offset + itemsPerPage);
        // Check for sparse array holes: count actual non-empty entries
        const expectedLen = Math.min(itemsPerPage, cache.totalRecords - offset);
        const actualLen = sliced.filter(item => item != null).length;
        if (actualLen >= expectedLen && expectedLen > 0) {
          const pages = Math.max(1, Math.ceil(cache.totalRecords / itemsPerPage));
          setCandidates(sliced.filter(item => item != null));
          setTotalRecords(cache.totalRecords);
          setTotalPages(pages);
          setLoading(false);
          return;
        }
      }
    }

    try {
      // Silent refresh (e.g. the overview Refresh button) keeps the current rows on screen instead of
      // flashing the skeleton — the table just updates in place once fresh data arrives.
      if (!silent) setLoading(true);

      // Standard page-based fetch: page=N, limit=L
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', String(itemsPerPage));
      if (sessionId) params.set('session_id', sessionId);
      if (sortBy) { params.set('sort_by', sortBy); params.set('sort_order', sortOrder); }
      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (activeFilter !== 'all') params.set('status', activeFilter);

      const response = await aiAutomationApi.get(`/automation_candidates/all?${params.toString()}`);

      let candidatesList = [];
      let serverTotal = 0;

      if (response && typeof response === 'object' && !Array.isArray(response) && response.data) {
        candidatesList = response.data || [];
        serverTotal = response.total_records || 0;
      } else if (Array.isArray(response)) {
        candidatesList = response;
        serverTotal = response.length;
      }

      console.log('[CandidateList] page:', currentPage, 'limit:', itemsPerPage, 'fetched:', candidatesList.length, 'total:', serverTotal);

      const parseJson = (val) => {
        if (!val) return null;
        if (Array.isArray(val)) return val;
        if (typeof val === 'object') return val;
        if (typeof val === 'string') { try { return JSON.parse(val); } catch { return null; } }
        return null;
      };

      const parsed = candidatesList.map((c) => {
        let skills = [];
        if (Array.isArray(c.skills)) skills = c.skills;
        else if (typeof c.skills === 'string') { try { skills = JSON.parse(c.skills); if (!Array.isArray(skills)) skills = []; } catch { skills = []; } }

        const isImported = String(c.source_type || c.channel || '').toLowerCase() === 'imported';
        let source = 'Unknown';
        const platforms = parseJson(c.platforms) || [];
        if (isImported) source = 'Imported';
        else if (platforms.length > 0) source = platforms[0];
        else if (c.source) source = typeof c.source === 'string' ? c.source : Array.isArray(c.source) ? c.source[0] || 'Unknown' : 'Unknown';

        // Imported candidates show "Imported" as their initial status until they progress past the
        // initial sourced stage (e.g. booked → Shortlisted), after which the real funnel stage shows.
        const _fsLower = String(c.funnel_stage || c.status || '').toLowerCase();
        const _isInitialStage = ['', 'sourced', 'identified', 'pending'].includes(_fsLower);
        const importedStatus = (isImported && _isInitialStage) ? 'imported' : (c.status || c.funnel_stage || 'pending');

        return {
          id: c.id || `candidate-${c.email}`,
          name: c.name || 'Unknown',
          title: c.headline || c.current_role || '',
          company: c.current_company || '',
          location: c.location || '',
          email: c.email || '',
          phone: c.phone || c.mobile_number || '',
          linkedin_profile_url: c.linkedin_profile_url || c.profile_url || '',
          match_percentage: c.match_percentage || null,
          score_details: parseJson(c.score_details) || c.score_details || {},
          skills: skills,
          experience_years: c.experience_years || c.years_of_experience || '',
          education: parseJson(c.education_records || c.education) || [],
          education_records: parseJson(c.education_records) || [],
          experience: parseJson(c.experience_records || c.experience) || [],
          experience_records: parseJson(c.experience_records) || [],
          source: formatSource(source),
          source_type: c.source_type || '',
          is_imported: isImported,
          status: importedStatus,
          funnel_stage: c.funnel_stage || c.status || '',
          rejection_reason: c.rejection_reason || '',
          headline: c.headline || '',
          current_role: c.current_role || '',
          current_company: c.current_company || '',
          summary: c.summary || c.summary_text || '',
          summary_text: c.summary_text || c.summary || '',
          highlights: parseJson(c.highlights) || [],
          match_breakdown: parseJson(c.match_breakdown) || {},
          platforms: parseJson(c.platforms) || [],
          priority: normalizeBoolean(c.priority),
          doc_id: c.doc_id || null,
          cv_filename: c.cv_filename || null,
          updatedAt: c.updated_at || c.created_at || null,
        };
      });

      // Accumulate data: merge new page data into the flat cache array
      const existing = cache?.allData || [];
      const newAllData = [...existing];
      // Place fetched records at the correct offset positions
      for (let i = 0; i < parsed.length; i++) {
        newAllData[offset + i] = parsed[i];
      }

      pageCacheRef.current[cacheKey] = { allData: newAllData, totalRecords: serverTotal };

      const pages = Math.max(1, Math.ceil(serverTotal / itemsPerPage));
      setCandidates(parsed);
      setTotalRecords(serverTotal);
      setTotalPages(pages);
    } catch (err) {
      console.error('Error loading candidates:', err);
      // On a silent refresh, keep whatever is already on screen instead of wiping to an empty table.
      if (!silent) {
        setCandidates([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [sessionId, currentPage, itemsPerPage, debouncedSearchTerm, activeFilter, sortBy, sortOrder]);

  // ── Load reply statuses (outreach data) ──
  const loadReplyStatuses = useCallback(async () => {
    if (!searchId || candidates.length === 0) return;

    const metadata = getMetadata();
    const userId = metadata?.uid || metadata?.userId || metadata?.id;
    if (!userId) return;

    try {
      const data = await aiAutomationApi.get(`/email/reply-status/${userId}/${searchId}`);

      if (data?.candidates) {
        setCandidatesByStatus({
          interested: data.candidates.interested || [],
          pending: data.candidates.pending || [],
          declined: data.candidates.declined || [],
        });
      }
      if (data?.all_replies) {
        setEmailReplies((prev) => ({ ...prev, ...data.all_replies }));
      }
    } catch (err) {
      console.error('Error loading reply statuses:', err);
    }
  }, [searchId, candidates.length]);

  useEffect(() => {
    if (activeTab === 'overview') {
      loadCandidates();
    }
  }, [loadCandidates, activeTab]);

  // Overview "Refresh" button bumps refreshToken → drop the page cache and silently re-pull the current
  // page so freshly-changed data (e.g. a candidate whose interview just completed → advanced funnel
  // stage / status) appears without navigating away and back to force a remount.
  useEffect(() => {
    if (!refreshToken) return;
    pageCacheRef.current = {};
    loadCandidates(true);
  }, [refreshToken, loadCandidates]);

  // Debounce search term to avoid excessive server requests
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
      pageCacheRef.current = {}; // Clear cache when search changes
    }, 400);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchTerm]);

  // ── Sort handler ──
  const handleSort = (columnKey) => {
    const backendField = columnSortKeys[columnKey];
    if (!backendField) return;
    if (sortBy === backendField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(backendField);
      setSortOrder('desc');
    }
    setCurrentPage(1);
    pageCacheRef.current = {}; // Clear cache when sort changes
  };

  // ── Sort icon indicator ──
  const SortIcon = ({ columnKey }) => {
    const backendField = columnSortKeys[columnKey];
    const isActive = sortBy === backendField;
    return (
      <span className="inline-flex flex-col ml-1" style={{ fontSize: '8px', lineHeight: '8px', verticalAlign: 'middle' }}>
        <span style={{ color: isActive && sortOrder === 'asc' ? '#111827' : '#D1D5DB' }}>▲</span>
        <span style={{ color: isActive && sortOrder === 'desc' ? '#111827' : '#D1D5DB' }}>▼</span>
      </span>
    );
  };

  useEffect(() => {
    if (candidates.length > 0 && searchId && !initialLoadDone.current) {
      initialLoadDone.current = true;
      loadReplyStatuses();
    }
  }, [candidates, searchId, loadReplyStatuses]);
  const candidatesWithStatus = useMemo(() => {
    // DB funnel_stage is authoritative. Subject-based mail sync can see
    // Gmail messages before outreach is sent, so reply buckets must not
    // visually promote Sourced candidates to Interested.
    const authoritativeStages = ['sourced', 'contacted', 'interested', 'shortlisted', 'interviewed', 'rejected', 'declined', 'hired'];
    
    return candidates.map((c) => {
      const emailLower = (c.email || '').toLowerCase();
      if (!emailLower) return c;

      // If funnel_stage is present, use it directly.
      const funnelStage = (c.funnel_stage || '').toLowerCase();
      if (authoritativeStages.includes(funnelStage)) {
        // Imported candidates at the initial sourced stage read "Imported", not "Sourced"
        // (this memo re-derives status from funnel_stage, so keep the imported override here too).
        const isImported = c.is_imported === true || String(c.source_type || '').toLowerCase() === 'imported';
        if (isImported && (funnelStage === 'sourced' || funnelStage === '')) {
          return { ...c, status: 'imported' };
        }
        return { ...c, status: funnelStage };
      }

      const allSynced = [
        ...candidatesByStatus.interested,
        ...candidatesByStatus.pending,
        ...candidatesByStatus.declined,
      ];
      const synced = allSynced.find(
        (sc) => (sc.candidate_email || '').toLowerCase() === emailLower
      );

      if (synced) {
        let status = 'pending';
        if (candidatesByStatus.interested.some((s) => (s.candidate_email || '').toLowerCase() === emailLower)) status = 'interested';
        else if (candidatesByStatus.declined.some((s) => (s.candidate_email || '').toLowerCase() === emailLower)) status = 'declined';
        return { ...c, status };
      }
      return c;
    });
  }, [candidates, candidatesByStatus]);

  // ── Derive last action and next step from replies ──
  const getLastAction = (email) => {
    const emailLower = (email || '').toLowerCase();
    const key = Object.keys(emailReplies).find((k) => k.toLowerCase() === emailLower);
    if (!key) return 'Outreach sent';
    const replies = emailReplies[key];
    if (!replies || replies.length === 0) return 'Outreach sent';
    const sorted = [...replies].sort((a, b) => new Date(b.received_at || b.sent_at || 0) - new Date(a.received_at || a.sent_at || 0));
    const last = sorted[0];
    if (last.is_sent_email) return last.is_nudge ? 'Follow-up sent' : 'Outreach sent';
    return 'Replied to outreach';
  };

  const hasCandidateResume = (candidate) => Boolean(
    candidate?.doc_id ||
    candidate?.cv_filename ||
    candidate?.resume_filename ||
    candidate?.attachment_location ||
    normalizeBoolean(candidate?.resume_uploaded) ||
    normalizeBoolean(candidate?.has_resume)
  );

  const getNextStep = (candidate) => {
    const s = (candidate.status || '').toLowerCase();
    const emailLower = (candidate.email || '').toLowerCase();

    if (['rejected', 'declined', 'hired'].includes(s)) return 'Closed';
    if (s === 'interested' && !hasCandidateResume(candidate)) return 'Request Resume';

    // Check if the candidate has an unanswered question (latest reply is from candidate)
    const key = Object.keys(emailReplies).find((k) => k.toLowerCase() === emailLower);
    if (key) {
      const replies = emailReplies[key];
      if (replies && replies.length > 0) {
        const sorted = [...replies].sort((a, b) => new Date(b.received_at || b.sent_at || 0) - new Date(a.received_at || a.sent_at || 0));
        const latest = sorted[0];
        // If latest is from candidate (not sent by us), they have an unanswered question
        if (!latest.is_sent_email) {
          // Check if a draft reply exists
          const hasDraft = candidate.question_draft_subject || candidate.question_draft_body;
          return hasDraft ? "Reply to Candidate's Question" : 'Create Draft Reply';
        }
      }
    }

    if (s === 'interested') return 'Schedule Interview';
    if (s === 'shortlisted') return 'Attend Interview';
    if (s === 'interviewed') return 'Review Candidate';
    if (s === 'declined') return 'Closed';
    return 'Send Follow-up';
  };

  // ── Filter + search ──
  const filteredCandidates = useMemo(() => {
    let list = candidatesWithStatus;

    if (activeFilter !== 'all') {
      list = list.filter((c) => {
        const fs = (c.funnel_stage || c.status || '').toLowerCase();
        if (activeFilter === 'interested') return fs === 'interested';
        if (activeFilter === 'shortlisted') return fs === 'shortlisted' || fs === 'interview';
        if (activeFilter === 'interviewed') return fs === 'interviewed' || fs === 'hired' || fs === 'finalised';
        if (activeFilter === 'declined') return fs === 'declined';
        if (activeFilter === 'rejected') return fs === 'rejected';
        return true;
      });
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (c) =>
          (c.name || '').toLowerCase().includes(q) ||
          (c.company || '').toLowerCase().includes(q) ||
          (c.title || '').toLowerCase().includes(q) ||
          (c.skills || []).some((sk) => sk.toLowerCase().includes(q))
      );
    }

    return list;
  }, [candidatesWithStatus, activeFilter, searchTerm]);
  // ── Filter + search (client-side — apply filter and search to candidatesWithStatus) ──
  const paginatedCandidates = filteredCandidates;

  // ── Pagination ──
  const handlePageChange = (p) => setCurrentPage(Math.max(1, Math.min(p, totalPages)));

  // ── Render ──
  if (loading) {
    return (
      <div className="w-full bg-white dark:bg-gray-900 rounded-xl shadow-md overflow-hidden transition-colors duration-250">
        <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 sm:px-6 sm:py-4">
          <div className="animate-pulse h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
        <div className="p-3 space-y-3 sm:p-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3 px-2 py-3 sm:gap-4 sm:px-4">
              <div className="animate-pulse w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="animate-pulse h-3.5 w-32 bg-gray-200 dark:bg-gray-700 rounded sm:w-40" />
                <div className="animate-pulse h-3 w-44 bg-gray-200 dark:bg-gray-700 rounded sm:w-56" />
              </div>
              <div className="animate-pulse h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white dark:bg-gray-900 rounded-xl shadow-md overflow-hidden transition-colors duration-250">
      {/* Header: Title + Filters + Search + Filter icon */}
      <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 sm:px-6 sm:py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                onClick={onBack}
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 transition-colors sm:h-8 sm:w-8"
                title="Back"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="text-base font-semibold text-gray-800 dark:text-white whitespace-nowrap sm:text-lg">
              Candidate List
            </h2>
          </div>

          <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
            {/* Filters */}
            <div className="flex w-full gap-2 overflow-x-auto pb-1 sm:w-auto sm:overflow-visible sm:pb-0">
              {filters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => { setActiveFilter(f.key); setCurrentPage(1); pageCacheRef.current = {}; }}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all duration-200 font-medium sm:px-4 sm:py-2 sm:text-sm ${
                    activeFilter === f.key
                      ? 'text-white'
                      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                  style={activeFilter === f.key ? { backgroundColor: primaryColor } : {}}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative flex min-w-0 flex-1 items-center sm:w-72 sm:flex-none">
              <input
                type="text"
                placeholder="Search Leads by name, company and roles..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); }}
                className="w-full px-4 py-2.5 pr-10 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-gray-300 dark:focus:border-gray-500 transition-colors duration-200 text-sm"
              />
              <svg className="w-5 h-5 absolute right-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <button
              type="button"
              onClick={() => setScoreEvidenceOpen(true)}
              disabled={candidates.length === 0}
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg border px-3 py-2.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
              style={{ borderColor: primaryColor, color: primaryColor, background: `${primaryColor}10` }}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16M4 12h16M4 19h16M8 5v14M16 5v14" />
              </svg>
              Score details
            </button>

            {/* Filter icon */}
            <button
              className="flex-shrink-0 p-2.5 rounded-lg transition-colors duration-200"
              style={{ border: `1px solid ${primaryColor}`, background: `${primaryColor}26`, color: primaryColor }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px]">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <th className="px-2 py-3 w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide cursor-pointer hover:text-gray-900 dark:hover:text-gray-200" onClick={() => handleSort('candidate')}>Candidate <SortIcon columnKey="candidate" /></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide cursor-pointer hover:text-gray-900 dark:hover:text-gray-200" onClick={() => handleSort('match')}>Resume match <SortIcon columnKey="match" /></th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide w-24">Source</th>
              <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide w-20">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide cursor-pointer hover:text-gray-900 dark:hover:text-gray-200" onClick={() => handleSort('resume_stats')}>Resume<SortIcon columnKey="resume_stats" /></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide">Experience</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide">Tags</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide">Last Action</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide">Next Step</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide">Last Updated</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap uppercase tracking-wide">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedCandidates.length > 0 ? (
              paginatedCandidates.map((c) => (
                <React.Fragment key={c.id || c.email}>
                <tr
                  id={`candidate-row-${c.id || c.email}`}
                  className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 cursor-pointer"
                  onClick={() => {
                    const rowId = c.id || c.email;
                    const isExpanding = expandedRowId !== rowId;
                    setExpandedRowId(isExpanding ? rowId : null);
                    if (isExpanding && onSidebarCollapse) onSidebarCollapse(true);
                    if (isExpanding) {
                      setTimeout(() => {
                        const row = document.getElementById(`candidate-row-${rowId}`);
                        if (row) {
                          // Find the scrollable parent (overflow-y-auto container)
                          let scrollParent = row.closest('[class*="overflow-y-auto"]') || row.closest('[class*="overflow-auto"]');
                          if (scrollParent) {
                            const rowTop = row.offsetTop - scrollParent.offsetTop;
                            scrollParent.scrollTo({ top: rowTop, behavior: 'smooth' });
                          } else {
                            row.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }
                      }, 100);
                    }
                  }}
                >
                  {/* Chevron */}
                  <td className="px-2 py-3 w-8">
                    <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedRowId === (c.id || c.email) ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </td>

                  {/* Candidate */}
                  <td className="px-4 py-3 max-w-[200px]">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 p-[3px]" style={{ border: '2px solid #F59E0B', background: '#FEF3C7' }}>
                        <img
                          src="/social_icons/candidate_profile.png"
                          alt={c.name || 'User'}
                          className="w-full h-full object-cover rounded-full"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{c.name}</div>
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                          {c.title}
                        </div>
                        {c.company && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {c.company}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Resume match */}
                  <td className="px-4 py-3">
                    {c.match_percentage ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="rounded-md px-2 py-1 text-xs font-bold"
                          style={{
                            color: Number(c.match_percentage) >= 80 ? '#16A34A' : Number(c.match_percentage) >= 60 ? '#D97706' : '#DC2626',
                            backgroundColor: Number(c.match_percentage) >= 80 ? '#DCFCE7' : Number(c.match_percentage) >= 60 ? '#FEF3C7' : '#FEE2E2',
                          }}
                          title="Resume/JD match score. The AI interview result score is shown inside Interview Report."
                        >
                          {c.match_percentage}%
                        </span>
                        <ScoreDetailsInfo details={c.score_details} candidateName={c.name || c.candidate_name || c.full_name} align="left" title="Resume Match Details" />
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>

                  {/* Source */}
                  <td className="px-3 py-3">
                    <SourceIcon source={c.source} />
                  </td>

                  {/* Status */}
                  <td className="px-2 py-3">
                    <StatusBadge status={c.status} />
                  </td>

                  {/* Resume Stats */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {hasCandidateResume(c) ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 whitespace-nowrap">
                        Submitted
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 whitespace-nowrap">
                        Not Submitted
                      </span>
                    )}
                  </td>

                  {/* Experience */}
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {c.experience_years ? `${c.experience_years} years` : '—'}
                  </td>

                  {/* Tags */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {(c.skills || []).length > 0 ? (
                        <>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 whitespace-nowrap">
                            {c.skills[0]}
                          </span>
                          {c.skills.length > 1 && (
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 whitespace-nowrap" title={c.skills.slice(1).join(', ')}>
                              +{c.skills.length - 1}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                  </td>

                  {/* Last Action */}
                  <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {c.is_imported && getLastAction(c.email) === 'Outreach sent' ? 'Imported' : getLastAction(c.email)}
                  </td>

                  {/* Next Step */}
                  <td className="px-4 py-3 text-xs font-medium whitespace-nowrap" style={{ color: primaryColor }}>
                    {getNextStep(c)}
                  </td>

                  {/* Contact icons */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {c.linkedin_profile_url && (
                        <a
                          href={c.linkedin_profile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="w-7 h-7 rounded flex items-center justify-center bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                          title="LinkedIn Profile"
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        </a>
                      )}
                      {c.email && (
                        <a
                          href={`mailto:${c.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="w-7 h-7 rounded flex items-center justify-center bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                          title={c.email}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                        </a>
                      )}
                    </div>
                  </td>

                  {/* Last Updated — use latest date from DB or email replies */}
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {(() => {
                      const emailLower = (c.email || '').toLowerCase();
                      const key = Object.keys(emailReplies).find((k) => k.toLowerCase() === emailLower);
                      let latestDate = c.updatedAt ? new Date(c.updatedAt) : null;
                      if (key && emailReplies[key]?.length > 0) {
                        const latestReply = emailReplies[key].reduce((latest, r) => {
                          const d = new Date(r.received_at || r.sent_at || 0);
                          return d > latest ? d : latest;
                        }, new Date(0));
                        if (!latestDate || latestReply > latestDate) latestDate = latestReply;
                      }
                      return formatTimeAgo(latestDate);
                    })()}
                  </td>

                  {/* Action — reject (hidden once terminal) */}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {(() => {
                      const fs = (c.funnel_stage || c.status || '').toLowerCase();
                      if (['declined', 'rejected', 'hired'].includes(fs)) {
                        return (
                          <span
                            className="text-xs text-gray-400"
                            title={fs === 'rejected' && c.rejection_reason ? `Rejected: ${c.rejection_reason}` : undefined}
                          >
                            {fs === 'rejected' && c.rejection_reason ? 'Rejected ⓘ' : '—'}
                          </span>
                        );
                      }
                      // Hide Reject while an interview is scheduled/pending (Shortlisted — booking advances
                      // here; it returns once the interview completes → Interviewed). Also hide it when the
                      // row is expanded, since the detail card below has its own gated Reject.
                      if (fs === 'shortlisted' || expandedRowId === (c.id || c.email)) return null;
                      return (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setRejectTarget(c); }}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2.5 py-1 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 dark:border-rose-900/50 dark:text-rose-400 dark:hover:bg-rose-900/20"
                          title="Reject candidate"
                        >
                          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" /></svg>
                          Reject
                        </button>
                      );
                    })()}
                  </td>
                </tr>
                {expandedRowId === (c.id || c.email) && (
                  <tr>
                    <td colSpan={13} className="p-0 border-b border-gray-200 dark:border-gray-700">
                      <div style={{ borderLeft: `3px solid ${primaryColor}` }} onClick={(e) => e.stopPropagation()}>
                        <CandidateDetail
                          candidate={{ ...c, _searchId: searchId }}
                          searchId={searchId}
                          sessionId={sessionId}
                          onBack={() => setExpandedRowId(null)}
                          onCandidateUpdate={handleCandidateUpdate}
                          onRejected={() => {
                            setExpandedRowId(null);
                            pageCacheRef.current = {};
                            loadCandidates();
                            onCandidateUpdate?.();
                          }}
                          jobTitle={jobTitle}
                          inline={true}
                          featureAccess={featureAccess}
                        />
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={13} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No candidates found</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Try adjusting your filters or search term</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalRecords > 0 && (
        <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
          <div className="flex items-center justify-between gap-3 sm:justify-start sm:gap-4">
            <label className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5 sm:text-sm sm:gap-2">
              Show
              <select
                className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none text-xs sm:text-sm"
                value={itemsPerPage >= 200 ? 'all' : itemsPerPage}
                onChange={(e) => { 
                  const val = e.target.value;
                  setItemsPerPage(val === 'all' ? 200 : Number(val)); 
                  setCurrentPage(1); 
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value="all">All</option>
              </select>
              entries
            </label>
            <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto pb-1 sm:gap-2 sm:overflow-visible sm:pb-0">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs sm:px-3 sm:text-sm"
            >
              &lt;
            </button>
            {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => {
              let pageNum;
              if ((totalPages || 1) <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= (totalPages || 1) - 2) pageNum = (totalPages || 1) - 4 + i;
              else pageNum = currentPage - 2 + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`min-w-[30px] h-8 px-2.5 rounded-lg font-medium text-xs transition-all duration-200 sm:min-w-[32px] sm:px-3 sm:text-sm ${
                    currentPage === pageNum ? 'text-white' : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                  style={currentPage === pageNum ? { background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, border: 'none' } : {}}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === (totalPages || 1)}
              className="px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs sm:px-3 sm:text-sm"
            >
              &gt;
            </button>
            </div>
          </div>
          <span className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalRecords)}-{Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} entries
          </span>
          </div>
      )}

      <ScoreEvidenceModal
        open={scoreEvidenceOpen}
        onClose={() => setScoreEvidenceOpen(false)}
        candidates={candidates}
        title={`${jobTitle || 'Candidates'} - Resume Match Details`}
        scoreKind="resume_match"
      />

      <RejectCandidateModal
        open={!!rejectTarget}
        candidate={rejectTarget}
        searchId={searchId}
        sessionId={sessionId}
        onClose={() => setRejectTarget(null)}
        onRejected={() => {
          setRejectTarget(null);
          setExpandedRowId(null);
          pageCacheRef.current = {};
          loadCandidates();
          onCandidateUpdate?.();
        }}
      />
    </div>
  );
};

export default CandidateList;
